import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { Console } from '@/kova/Console'
import type { ConsoleConfig } from '@/lib/env/config'

import { ConsoleProvider } from './ConsoleContext'
import { ErrorBoundary } from './ErrorBoundary'

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Operators keep tabs open for hours; stale data behind a fresh-looking
        // screen is how a support agent quotes a balance that has moved.
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: 1,
      },
      // Money-adjacent instructions are never retried automatically. Retrying
      // without honouring the caller's idempotency key is invariant 5's failure
      // mode; the operator decides.
      mutations: { retry: false },
    },
  })
}

export function App({ config }: { config: ConsoleConfig }) {
  const queryClient = useMemo(createQueryClient, [])

  return (
    <ErrorBoundary>
      <ConsoleProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <Routes>
            {/* The screen id is the route. Every filter state belongs in the
                URL so an operator can hand a colleague the exact queue they
                are looking at rather than directions to it. */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/:screenId" element={<Console config={config} />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </QueryClientProvider>
      </ConsoleProvider>
    </ErrorBoundary>
  )
}
